import cn from "classnames";
import React, { useCallback, useMemo, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useMeCore } from "../../../../cores/me";
import { useSocialPostCore } from "../../../../cores/socialPost";
import {
  SocialProviderId,
  getSocialPostRules,
  socialProviderIdsMatch,
  isInstagramSocialConnected,
} from "../../../../cores/socialPost/config";
import { SOCIAL_POST_TYPES } from "../../../../cores/socialPost/consts/socialPostType.constant";
import { PostErrorType } from "../../../../cores/socialPost/interfaces/postError";
import { SocialProvider } from "../../../../cores/socialPost/interfaces/socialProvider";
import { SocialType } from "../../../../cores/socialPost/interfaces/postError";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faExclamationTriangle,
  faPlay,
  faPlus,
  faTh,
} from "@fortawesome/free-solid-svg-icons";
import { PostType } from "../../../../cores/socialPost/enums/postType";
import { FacebookIcon } from "../../social/Facebook/FacebookIcon";
import { InstagramIcon } from "../../social/Instagram/InstagramIcon";
import { TwitterIcon } from "../../social/Twitter/TwitterIcon";
import { LinkedInIcon } from "../../social/LinkedIn/LinkedInIcon";
import { TiktokIcon } from "../../social/Tiktok/TiktokIcon";
import { YoutubeIcon } from "../../social/Youtube/YoutubeIcon";
import { ConnectSocialModal } from "./ConnectSocialModal";
import { isTiktokPostingEnabled } from "../../../../cores/config/featureFlags";
import { isYoutubePostingEnabled } from "../../../../cores/config/featureFlags";
import { isYoutubeConnected, useYoutubeCore } from "../../../../cores/youtube";
import "./ModernSocialPostSelector.scss";

interface Props {
  socialType?: SocialType;
  hideTitle?: boolean;
  /** Editar / duplicar: solo la red del post, sin poder cambiar ni conectar otras. */
  lockProviders?: boolean;
}

export const ModernSocialPostSelector = ({
  socialType = SocialType.Post,
  hideTitle = false,
  lockProviders = false,
}: Props) => {
  const intl = useIntl();
  const { socialSettings } = useMeCore();
  const { settings: youtubeSettings } = useYoutubeCore();
  const {
    socialProviders,
    postErrors,
    images,
    video,
    selectedPost,
    postType,
    setPostTypeAction,
    removeVideo,
    removeAllImages,
    removePostAllMedia,
    addSocialProvider,
    removeSocialProvider,
    clearSocialProviders,
    addPostError,
    removePostError,
  } = useSocialPostCore();

  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showInstagramDropdown, setShowInstagramDropdown] = useState(false);
  const [showYoutubeDropdown, setShowYoutubeDropdown] = useState(false);


  const hasError = useCallback(
    (id: string, errorType: PostErrorType) =>
      (postErrors as any[]).some(
        ({ type, socialProviderId }: any) =>
          socialProviderIdsMatch(socialProviderId, id) && type === errorType
      ),
    [postErrors]
  );

  const isSelected = useCallback(
    (id: string) => (socialProviders as any[]).some((sp: any) => sp.id === id),
    [socialProviders]
  );

  const imageNotAllowedProviders = useMemo(
    () =>
      (socialProviders as SocialProvider[]).filter(({ id }) => {
        const rules = getSocialPostRules(id);
        return rules?.imageAllowed === false;
      }),
    [socialProviders]
  );

  useEffect(() => {
    if (socialType !== SocialType.Post) return;

    imageNotAllowedProviders.forEach(({ id }) => {
      const hasImages =
        images.length > 0 ||
        (selectedPost?.post?.type === SOCIAL_POST_TYPES.IMAGE &&
          (selectedPost?.media?.length ?? 0) > 0);

      hasImages
        ? !hasError(id, PostErrorType.ImageNotAllowed) &&
          addPostError({
            socialProviderId: id as SocialProviderId,
            type: PostErrorType.ImageNotAllowed,
            message: intl.formatMessage({
              id: "app.socialMediaPost.errorImageNotAllowedTikTok",
            }),
          })
        : hasError(id, PostErrorType.ImageNotAllowed) &&
          removePostError(
            id as SocialProviderId,
            PostErrorType.ImageNotAllowed
          );
    });
  }, [
    socialType,
    imageNotAllowedProviders,
    images.length,
    images,
    selectedPost,
    addPostError,
    removePostError,
    hasError,
    intl,
  ]);

  const socialProvidersAbleToPost: SocialProvider[] = useMemo(() => {
    const providers: SocialProvider[] = [];
    
    if ((socialSettings as any)?.facebook) {
      providers.push({
        id: SocialProviderId.Facebook,
        name: SocialProviderId.Facebook,
        displayName: "Facebook",
        icon: "facebook",
        color: "#1877F2",
      });
    }
    
    if (isInstagramSocialConnected(socialSettings as Record<string, unknown>)) {
      providers.push({
        id: SocialProviderId.Instagram,
        name: SocialProviderId.Instagram,
        displayName: "Instagram",
        icon: "instagram",
        color: "#E4405F",
      });
    }
    
    if ((socialSettings as any)?.twitter) {
      providers.push({
        id: SocialProviderId.Twitter,
        name: SocialProviderId.Twitter,
        displayName: "Twitter",
        icon: "twitter",
        color: "#1DA1F2",
      });
    }
    
    if ((socialSettings as any)?.linkedIn) {
      providers.push({
        id: SocialProviderId.LinkedIn,
        name: SocialProviderId.LinkedIn,
        displayName: "LinkedIn",
        icon: "linkedIn",
        color: "#0077B5",
      });
    }
    
    if ((socialSettings as any)?.linkedInBusiness) {
      providers.push({
        id: SocialProviderId.LinkedInBusiness,
        name: SocialProviderId.LinkedInBusiness,
        displayName: "LinkedIn Business",
        icon: "linkedInBusiness",
        color: "#0077B5",
      });
    }
    
    if (isTiktokPostingEnabled() && (socialSettings as any)?.tiktok) {
      providers.push({
        id: SocialProviderId.TikTok,
        name: SocialProviderId.TikTok,
        displayName: "TikTok",
        icon: "tiktok",
        // Rosa marca TikTok: legible en claro y en oscuro (el negro desaparece sobre #0f141b)
        color: "#fe2c55",
      });
    }

    if (
      isYoutubePostingEnabled() &&
      ((socialSettings as any)?.youtube || isYoutubeConnected(youtubeSettings))
    ) {
      providers.push({
        id: SocialProviderId.YouTube,
        name: SocialProviderId.YouTube,
        displayName: "YouTube",
        icon: "youtube",
        color: "#FF0000",
      });
    }

    return providers;
  }, [socialSettings, youtubeSettings]);

  // Solo al editar (lock): forzar la red original. Al duplicar/crear se pueden elegir varias.
  useEffect(() => {
    if (!lockProviders) return;
    if (!selectedPost?.post?.socialProvider) return;
    const rawId = selectedPost.post.socialProvider;
    const provider = socialProvidersAbleToPost.find((p) =>
      socialProviderIdsMatch(p.id, rawId)
    );
    if (!provider) return;
    clearSocialProviders();
    addSocialProvider(provider);
  }, [
    lockProviders,
    selectedPost,
    socialProvidersAbleToPost,
    clearSocialProviders,
    addSocialProvider,
  ]);

  const getProviderIcon = (provider: SocialProvider) => {
    const iconProps = {
      size: "1x" as const,
      color: isSelected(provider.id) ? "#ffffff" : provider.color,
    };

    switch (provider.id) {
      case SocialProviderId.Facebook:
        return <FacebookIcon {...iconProps} />;
      case SocialProviderId.Instagram:
        return <InstagramIcon {...iconProps} />;
      case SocialProviderId.Twitter:
        return <TwitterIcon {...iconProps} />;
      case SocialProviderId.LinkedIn:
        return <LinkedInIcon {...iconProps} />;
      case SocialProviderId.LinkedInBusiness:
        return <LinkedInIcon {...iconProps} />;
      case SocialProviderId.TikTok:
        return <TiktokIcon {...iconProps} />;
      case SocialProviderId.YouTube:
        return <YoutubeIcon {...iconProps} />;
      default:
        return null;
    }
  };

  const providersLocked = lockProviders;

  const visibleProviders = useMemo(() => {
    if (!providersLocked || !selectedPost?.post?.socialProvider) {
      return socialProvidersAbleToPost;
    }
    const rawId = selectedPost.post.socialProvider;
    return socialProvidersAbleToPost.filter((p) =>
      socialProviderIdsMatch(p.id, rawId)
    );
  }, [providersLocked, selectedPost, socialProvidersAbleToPost]);

  const handleProviderClick = (provider: SocialProvider) => {
    if (providersLocked) return;
    if (isSelected(provider.id)) {
      removeSocialProvider(provider);
    } else {
      addSocialProvider(provider);
      if (provider.id === SocialProviderId.YouTube) {
        setPostTypeAction(PostType.VIDEO);
      }
      if (
        provider.id === SocialProviderId.Instagram &&
        (video ||
          selectedPost?.post?.type === SOCIAL_POST_TYPES.VIDEO ||
          postType === PostType.REEL)
      ) {
        setPostTypeAction(PostType.REEL);
      }
    }
  };

  const handleConnectSocial = (providerId: string) => {
    // El modal de conexión maneja la autenticación directamente
    // Solo cerramos el modal después de la selección
    setShowConnectModal(false);
  };

  const handleInstagramClick = (provider: SocialProvider) => {
    // Instagram se puede seleccionar/deseleccionar normalmente
    handleProviderClick(provider);
  };

  const handlePostTypeSelect = (nextPostType: PostType) => {
    if (providersLocked) return;
    setPostTypeAction(nextPostType);
    if (nextPostType === PostType.REEL || nextPostType === PostType.VIDEO || nextPostType === PostType.SHORT) {
      removeAllImages();
      if (selectedPost?.media?.length) {
        removePostAllMedia();
      }
    } else {
      removeVideo();
      if (selectedPost?.media?.length) {
        removePostAllMedia();
      }
    }
    setShowInstagramDropdown(false);
    setShowYoutubeDropdown(false);
  };

  const getInstagramPostTypeDisplay = () => {
    return postType === PostType.POST ? 'Post' : 'Reel';
  };

  const getYoutubePostTypeDisplay = () => {
    return postType === PostType.SHORT ? 'Short' : 'Video';
  };

  const showInstagramVideoReelWarning =
    isSelected(SocialProviderId.Instagram) &&
    postType === PostType.POST &&
    Boolean(
      video ||
        (selectedPost?.post?.type === SOCIAL_POST_TYPES.VIDEO &&
          selectedPost?.media?.length)
    );

  // Cerrar dropdown cuando Instagram se deselecciona
  useEffect(() => {
    const isInstagramSelected = isSelected(SocialProviderId.Instagram);
    if (!isInstagramSelected) {
      setShowInstagramDropdown(false);
    }
    const isYoutubeSelected = isSelected(SocialProviderId.YouTube);
    if (!isYoutubeSelected) {
      setShowYoutubeDropdown(false);
    }
  }, [socialProviders, isSelected]);

  return (
    <div
      className={cn("modern-social-selector", {
        "modern-social-selector--locked": providersLocked,
      })}
    >
      {!hideTitle && (
        <div className="selector-header">
          <h4 className="selector-title">
            {intl.formatMessage({ id: "app.socialPostSelector.createTitle" })}
          </h4>
        </div>
      )}
      <div className="providers-container">
        {visibleProviders.map((provider) => {
          const selected = isSelected(provider.id);
          const hasAnyError = (postErrors as any[]).some(
            ({ socialProviderId }: any) =>
              socialProviderIdsMatch(socialProviderId, provider.id)
          );

          // Instagram con botón de tipo separado
          if (provider.id === SocialProviderId.Instagram) {
            return (
              <React.Fragment key={provider.id}>
                {/* Botón de Instagram */}
                <button
                  className={`provider-button ${selected ? 'selected' : ''} ${hasAnyError ? 'error' : ''}`}
                  onClick={() => handleInstagramClick(provider)}
                  disabled={providersLocked}
                  style={{
                    '--provider-color': provider.color,
                  } as React.CSSProperties}
                >
                  <div className="provider-icon">
                    {getProviderIcon(provider)}
                  </div>
                  <span className="provider-name">{provider.displayName}</span>
                </button>

                {/* Botón de tipo de post para Instagram */}
                {selected && (
                  <div className="instagram-post-type-container">
                    <button
                      className="post-type-button"
                      onClick={() => setShowInstagramDropdown(!showInstagramDropdown)}
                      disabled={providersLocked}
                    >
                      <span className="post-type-text">{getInstagramPostTypeDisplay()}</span>
                      <FontAwesomeIcon 
                        icon={faChevronDown} 
                        className={`chevron ${showInstagramDropdown ? 'rotated' : ''}`}
                      />
                    </button>

                    {/* Dropdown para tipos de post */}
                    {showInstagramDropdown && !providersLocked && (
                      <div className="instagram-post-type-dropdown">
                        <div
                          className={`dropdown-item ${postType === PostType.POST ? 'selected' : ''}`}
                          onClick={() => handlePostTypeSelect(PostType.POST)}
                        >
                          <div className="item-left">
                            <FontAwesomeIcon icon={faTh} className="item-icon" />
                            <div className="item-content">
                              <div className="item-title">Post</div>
                              <div className="item-description">{intl.formatMessage({ id: "app.socialPostSelector.postDescription" })}</div>
                            </div>
                          </div>
                          {postType === PostType.POST && (
                            <FontAwesomeIcon icon={faCheck} className="check-icon" />
                          )}
                        </div>
                        
                        <div
                          className={`dropdown-item ${postType === PostType.REEL ? 'selected' : ''}`}
                          onClick={() => handlePostTypeSelect(PostType.REEL)}
                        >
                          <div className="item-left">
                            <FontAwesomeIcon icon={faPlay} className="item-icon" />
                            <div className="item-content">
                              <div className="item-title">Reel</div>
                              <div className="item-description">{intl.formatMessage({ id: "app.socialPostSelector.reelDescription" })}</div>
                            </div>
                          </div>
                          {postType === PostType.REEL && (
                            <FontAwesomeIcon icon={faCheck} className="check-icon" />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          }

          if (provider.id === SocialProviderId.YouTube) {
            return (
              <React.Fragment key={provider.id}>
                <button
                  className={`provider-button ${selected ? 'selected' : ''} ${hasAnyError ? 'error' : ''}`}
                  onClick={() => handleProviderClick(provider)}
                  disabled={providersLocked}
                  style={{
                    '--provider-color': provider.color,
                  } as React.CSSProperties}
                >
                  <div className="provider-icon">
                    {getProviderIcon(provider)}
                  </div>
                  <span className="provider-name">{provider.displayName}</span>
                </button>

                {selected && (
                  <div className="instagram-post-type-container">
                    <button
                      className="post-type-button"
                      onClick={() => setShowYoutubeDropdown(!showYoutubeDropdown)}
                      disabled={providersLocked}
                    >
                      <span className="post-type-text">{getYoutubePostTypeDisplay()}</span>
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`chevron ${showYoutubeDropdown ? 'rotated' : ''}`}
                      />
                    </button>

                    {showYoutubeDropdown && !providersLocked && (
                      <div className="instagram-post-type-dropdown">
                        <div
                          className={`dropdown-item ${postType === PostType.VIDEO ? 'selected' : ''}`}
                          onClick={() => handlePostTypeSelect(PostType.VIDEO)}
                        >
                          <div className="item-left">
                            <FontAwesomeIcon icon={faPlay} className="item-icon" />
                            <div className="item-content">
                              <div className="item-title">Video</div>
                              <div className="item-description">{intl.formatMessage({ id: "app.youtube.videoDescription" })}</div>
                            </div>
                          </div>
                          {postType === PostType.VIDEO && (
                            <FontAwesomeIcon icon={faCheck} className="check-icon" />
                          )}
                        </div>

                        <div
                          className={`dropdown-item ${postType === PostType.SHORT ? 'selected' : ''}`}
                          onClick={() => handlePostTypeSelect(PostType.SHORT)}
                        >
                          <div className="item-left">
                            <FontAwesomeIcon icon={faPlay} className="item-icon" />
                            <div className="item-content">
                              <div className="item-title">Short</div>
                              <div className="item-description">{intl.formatMessage({ id: "app.youtube.shortDescription" })}</div>
                            </div>
                          </div>
                          {postType === PostType.SHORT && (
                            <FontAwesomeIcon icon={faCheck} className="check-icon" />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          }

          // Otros proveedores (Facebook, TikTok, etc.)
          return (
            <button
              key={provider.id}
              type="button"
              className={cn("provider-button", {
                selected,
                error: hasAnyError,
                "provider-button--tiktok":
                  provider.id === SocialProviderId.TikTok,
              })}
              onClick={() => handleProviderClick(provider)}
              disabled={providersLocked}
              style={{
                "--provider-color": provider.color,
              } as React.CSSProperties}
            >
              <div className="provider-icon">
                {getProviderIcon(provider)}
              </div>
              <span className="provider-name">{provider.displayName}</span>
            </button>
          );
        })}
        
        {/* Botón para conectar más redes sociales */}
        {!providersLocked && (
          <button
            className="provider-button add-provider-button"
            onClick={() => setShowConnectModal(true)}
          >
            <div className="provider-icon">
              <FontAwesomeIcon icon={faPlus} />
            </div>
            <span className="provider-name">{intl.formatMessage({ id: "app.socialPostSelector.connectMore" })}</span>
          </button>
        )}
      </div>

      {showInstagramVideoReelWarning && (
        <div className="instagram-reel-warning" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>
            {intl.formatMessage({
              id: "app.socialPostSelector.videoRequiresReel",
            })}
          </span>
        </div>
      )}
      
      {/* Modal para conectar redes sociales */}
      <ConnectSocialModal
        isOpen={showConnectModal}
        toggle={() => setShowConnectModal(false)}
        onConnect={handleConnectSocial}
      />
    </div>
  );
};
